import { Link } from "react-router";
import { Check, Eye } from "lucide-react";
import type { Template } from "../../lib/api";
import { subcategoryLabel } from "../../lib/taxonomy";
import { coverSrcSet, coverUrl, templateCover, type CoverImage } from "../../lib/template-art";
import { Badge } from "../ui/Badge";

/** Lazy, decorative cover photo sized for its slot. */
export function Cover({
  image,
  width,
  height,
  eager = false,
}: {
  image: CoverImage;
  width: number;
  height: number;
  eager?: boolean;
}) {
  return (
    <img
      className="cover"
      src={coverUrl(image, width, height)}
      srcSet={coverSrcSet(image, width, height)}
      width={width}
      height={height}
      alt=""
      loading={eager ? "eager" : "lazy"}
      decoding="async"
    />
  );
}

/** Mock browser chrome above a cover, so covers read as websites. */
export function BrowserBar({ url }: { url: string }) {
  return (
    <div className="browser-bar" aria-hidden="true">
      <div className="browser-bar__dots">
        <span />
        <span />
        <span />
      </div>
      <div className="browser-bar__url">{url}</div>
    </div>
  );
}

function urlFor(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${slug || "your-site"}.zirro.site`;
}

/**
 * Gallery card: framed cover with a hover "Preview" overlay link, then the
 * name, description, and a "Use this template" call to action.
 */
export function TemplateCard({
  template,
  previewHref,
  useHref,
  headingLevel = 2,
}: {
  template: Template;
  previewHref: string;
  useHref: string;
  headingLevel?: 2 | 3;
}) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <article className="tpl-card">
      <div className="tpl-card__frame">
        <BrowserBar url={urlFor(template.name)} />
        <div className="tpl-card__media">
          <Cover image={templateCover(template)} width={640} height={480} />
          <div className="tpl-card__tag">
            <Badge variant="primary">{template.kind === "store" ? "Online store" : subcategoryLabel(template.subcategory)}</Badge>
          </div>
          <Link className="tpl-card__preview" to={previewHref} aria-label="Preview">
            <span>
              <Eye size={16} aria-hidden="true" />
              Preview
            </span>
          </Link>
        </div>
      </div>
      <div className="tpl-card__body">
        <div>
          <Heading>{template.name}</Heading>
          <p>{template.description}</p>
        </div>
        <Link className="btn btn--primary btn--sm" to={useHref}>
          Use this template
        </Link>
      </div>
    </article>
  );
}

/** Selectable card for the onboarding design step. */
export function SelectableTemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: Template;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="tpl-card" aria-pressed={selected} onClick={onSelect}>
      <div className="tpl-card__frame">
        <BrowserBar url={urlFor(template.name)} />
        <div className="tpl-card__media">
          <Cover image={templateCover(template)} width={480} height={360} />
          {selected ? (
            <span className="tpl-card__selected" aria-hidden="true">
              <Check size={16} />
            </span>
          ) : null}
        </div>
      </div>
      <div className="tpl-card__body">
        <div>
          <h2>{template.name}</h2>
          <p>{template.description}</p>
        </div>
      </div>
    </button>
  );
}

/** Photo tile used as a category filter (gallery) or picker (onboarding). */
export function CategoryTile({
  image,
  label,
  hint,
  pressed,
  onClick,
}: {
  image: CoverImage;
  label: string;
  hint?: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="cat-tile"
      aria-pressed={pressed}
      aria-label={label}
      onClick={onClick}
    >
      <Cover image={image} width={320} height={320} eager />
      <span className="cat-tile__check" aria-hidden="true">
        <Check size={14} />
      </span>
      <span className="cat-tile__label">{label}</span>
      {hint ? <span className="cat-tile__hint">{hint}</span> : null}
    </button>
  );
}
